import { ChangeControlSeverity } from 'src/generated/graphql';
import { ActiveChangeControlWindow } from 'src/common/ui/ChangeControlBanner/useChangeControlStatus';

// The change-control override (MCM) contract shared by every submission surface
// that can be gated — `SubmitTranslationModal` for a routine submit and
// `RollbackModal` for a rollback.
//
// It lives in one module rather than once per modal because the two surfaces
// answer to the SAME backend gate (`ChangeControlGate`): a regex, a placeholder
// or an error sentence that drifted between them would present one submission
// path as stricter or laxer than the other while the gate treats them
// identically.

/**
 * Mirrors the backend's `McmIdentifier`: `^MCM-[0-9]+$` with `@length(min: 5,
 * max: 32)`, so 1–28 digits after the 4-character `MCM-` prefix. A pre-check
 * only, so an obviously malformed id is caught before a round trip; validity
 * (exists, approved, in window) is decided by the backend against the MCM
 * service. Case-sensitive on purpose: MCM ids are upper-case, and the backend
 * pattern is too.
 */
export const MCM_ID_REGEX = /^MCM-[0-9]{1,28}$/;

export const INVALID_MCM_ID_ERROR_KEY = 'sgd-mcm-id-invalid';
export const OVERRIDE_REQUIRED_ERROR_KEY = 'sgd-mcm-required';
export const MCM_ID_DESCRIPTION_KEY = 'sgd-mcm-id-help';

/** Doubles as the field's test handle. Not a resource: an example MCM id is an identifier and
 * reads the same in every locale. */
export const MCM_ID_PLACEHOLDER = 'MCM-12345678';

/**
 * Keyed by severity: a Blocked window and a Restricted one are the same mechanism with different
 * stakes, and the header is what tells them apart.
 */
export const WINDOW_LABEL_KEY: Record<
  ActiveChangeControlWindow['severity'],
  string
> = {
  // Reuses the app-wide banner headers: a duplicate resource would be translated twice and drift.
  [ChangeControlSeverity.Blocked]: 'cc-banner-header-blocked',
  [ChangeControlSeverity.Restricted]: 'cc-banner-header-restricted',
};

/**
 * Resource id of the error to show beneath the MCM field, or `undefined` when
 * there is nothing to complain about.
 *
 * Hidden field → always `undefined`: an exempt submission has no MCM to collect,
 * so it must never be reported as invalid. Shown field → REQUIRED, because the
 * only reason it is shown is that the gate will refuse the submission without
 * one; a blank is the `OVERRIDE_REQUIRED` refusal the backend would answer with,
 * caught locally instead.
 */
export function mcmOverrideErrorKey(
  fieldVisible: boolean,
  trimmedMcmId: string,
): string | undefined {
  if (!fieldVisible) {
    return undefined;
  }
  if (trimmedMcmId === '') {
    return OVERRIDE_REQUIRED_ERROR_KEY;
  }
  return MCM_ID_REGEX.test(trimmedMcmId) ? undefined : INVALID_MCM_ID_ERROR_KEY;
}
